import React, { useState } from 'react';
import { Sparkles, Loader2, Wand2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { useApp } from '../../context/AppContext';
import { useToast } from '../../hooks/use-toast';
import { api, apiError } from '../../lib/api';
import { LANGUAGES } from '../../lib/i18n';

export const AiWriter = ({ context, onApply }) => {
  const { t, lang } = useApp();
  const { toast } = useToast();
  const [keywords, setKeywords] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const generate = async () => {
    if (keywords.trim().length < 2) return;
    setLoading(true);
    try {
      const { data } = await api.post('/ai/listing-copy', { keywords, ...context });
      setResult(data);
      onApply(data[lang] || data.en);
      toast({ title: t('post.aiDone') });
    } catch (e) {
      console.error('AI copy failed', e);
      toast({ title: t('post.aiError'), description: apiError(e) });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="rounded-2xl border border-[#F2B441]/50 bg-[#F2B441]/10 p-5" data-testid="ai-writer">
      <div className="flex items-center gap-2 mb-1">
        <div className="w-8 h-8 rounded-lg bg-[#F2B441] flex items-center justify-center"><Wand2 className="w-4 h-4 text-[#1A1A1A]" /></div>
        <h3 className="font-bold text-[#1A1A1A]">{t('post.aiTitle')}</h3>
      </div>
      <p className="text-xs text-[#7A7468] mb-3">{t('post.aiHint')}</p>
      <div className="flex flex-col sm:flex-row gap-2">
        <Input value={keywords} onChange={(e) => setKeywords(e.target.value)} placeholder={t('post.aiKeywords')} className="h-11 bg-white" data-testid="ai-keywords-input"
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); generate(); } }} />
        <Button type="button" onClick={generate} disabled={loading || keywords.trim().length < 2} className="h-11 bg-[#1A1A1A] hover:bg-[#0F8B5C] text-white shrink-0" data-testid="ai-generate-button">
          {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
          {loading ? t('post.aiGenerating') : t('post.aiGenerate')}
        </Button>
      </div>
      {result && (
        <div className="flex items-center gap-2 mt-3 text-xs" data-testid="ai-result-langs">
          <span className="text-[#7A7468]">{t('post.aiApply')}:</span>
          {LANGUAGES.map((l) => (
            <button key={l.code} type="button" onClick={() => onApply(result[l.code])} data-testid={`ai-apply-${l.code}`} className="px-2.5 py-1 rounded-full bg-white border border-[#EFEAE0] hover:border-[#0F8B5C] font-semibold">{l.flag} {l.code.toUpperCase()}</button>
          ))}
        </div>
      )}
    </div>
  );
};
